import React from "react";
import { ReactComponent as NotStreakIcon } from "../../../../../assects/svg/Insight/Not_streak_icon.svg";
import { ReactComponent as PartialLogStreakIcon } from "../../../../../assects/svg/Insight/Prtial_log_streak_icon.svg";
import { ReactComponent as LogStreakIcon } from "../../../../../assects/svg/Insight/Log_streak_icon.svg";

type DayLogStatus = "none" | "partial" | "logged" | "upcoming";

interface MonthlyStreakCardProps {
  currentStreak?: number;
  longestStreak?: number;
  daysMissed?: number;
  monthStart?: Date;
  dayLogs?: DayLogStatus[];
}

const GrayCircle = () => (
  <div className="w-7 h-7 3xl:w-8 3xl:h-8 rounded-full bg-[#E5E7EB]" />
);

const MonthlyStreakCard: React.FC<MonthlyStreakCardProps> = ({
  currentStreak,
  longestStreak,
  daysMissed,
  monthStart,
  dayLogs,
}) => {
  const isLoading = dayLogs == null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthAnchor = (() => {
    const d = monthStart ? new Date(monthStart) : new Date(today);
    d.setHours(0, 0, 0, 0);
    if (!monthStart) d.setDate(1);
    return d;
  })();

  const daysInMonth = new Date(
    monthAnchor.getFullYear(),
    monthAnchor.getMonth() + 1,
    0,
  ).getDate();

  const getEffectiveStatus = (idx: number): DayLogStatus => {
    const dayDate = new Date(monthAnchor);
    dayDate.setDate(monthAnchor.getDate() + idx);
    dayDate.setHours(0, 0, 0, 0);

    if (dayDate > today) {
      const status = dayLogs?.[idx];
      return status === "logged" || status === "partial" ? status : "upcoming";
    }
    return dayLogs?.[idx] ?? "none";
  };

  const renderDayIcon = (status: DayLogStatus) => {
    switch (status) {
      case "logged":
        return <LogStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "partial":
        return <PartialLogStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "none":
        return <NotStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "upcoming":
        return <GrayCircle />;
    }
  };

  const statBoxBase = "bg-[#F5F6F7] rounded-lg px-4 py-3";
  const statLabelClass =
    "text-[12px] 3xl:text-[13px] text-[#8D8D8D] font-inter";
  const statValueClass =
    "text-[16px] 3xl:text-[18px] font-semibold text-[#1F1F1F] font-inter";

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter mb-3">
        This Month Streak
      </span>

      <div className="grid grid-cols-12 gap-4 items-start">
        {/* Left: stat boxes */}
        <div className="col-span-4 2xl:col-span-5 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className={`${statBoxBase} flex flex-col gap-1`}>
              <span className={statLabelClass}>Current streak</span>
              <span className={statValueClass}>
                {isLoading ? (
                  <span className="inline-block w-14 h-5 rounded bg-[#E5E7EB]" />
                ) : (
                  `${currentStreak ?? 0} days`
                )}
              </span>
            </div>
            <div className={`${statBoxBase} flex flex-col gap-1`}>
              <span className={statLabelClass}>Days missed</span>
              <span className={statValueClass}>
                {isLoading ? (
                  <span className="inline-block w-8 h-5 rounded bg-[#E5E7EB]" />
                ) : (
                  daysMissed ?? 0
                )}
              </span>
            </div>
          </div>
          <div className={`${statBoxBase} flex items-center justify-between`}>
            <span className={statLabelClass}>Longest streak</span>
            <span className={statValueClass}>
              {isLoading ? (
                <span className="inline-block w-14 h-5 rounded bg-[#E5E7EB]" />
              ) : (
                `${longestStreak ?? 0} days`
              )}
            </span>
          </div>
        </div>

        {/* Right: day grid — 3 rows. 31-day months use 11 cols, others use 10. */}
        <div
          className={`col-span-8 2xl:col-span-7 grid ${
            daysInMonth === 31 ? "grid-cols-11" : "grid-cols-10"
          } gap-x-2 gap-y-3`}
        >
          {Array.from({ length: daysInMonth }, (_, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              {isLoading ? <GrayCircle /> : renderDayIcon(getEffectiveStatus(i))}
              <span className="text-[10px] 3xl:text-[11px] text-[#8E8E93] font-inter leading-none">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MonthlyStreakCard;
